import { SegmentedControl } from "@mantine/core"
import React, { useContext, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"

export const BillingPeriodSelector = ({
	showDiscounts = false,
	billingPeriod,
	setBillingPeriod,
	disableDiscountsForCartItems,
	additionalSavings = null,
}) => {
	const { state } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const anchorRef = useRef(null)
	const labelRef = useRef(null)
	const [pos, setPos] = useState({ top: 0, left: 0 })

	const discountBackgroundColor = disableDiscountsForCartItems
		? "#f76868"
		: "#cbffb2"
	const discountTextColor = disableDiscountsForCartItems
		? "#840101"
		: "#215109"
	const discountBoxShadow = disableDiscountsForCartItems
		? "0 0 0 1px #e3484880"
		: "0 0 0 1px #72fd4e80,0 16px 8px #72fd4e03,0 12px 6px #72fd4e0a,0 4px 4px #72fd4e12,0 1.5px 3px #72fd4e14,inset 0 1px 2px #ffffff1f"

	// Savings badge for the cheaper yearly/quarterly additional-stores
	// experiment (test arm). Takes precedence over new-user discount badges.
	const savingsBadge = (percentage) => (
		<div
			style={{
				fontSize: "0.65rem",
				padding: "0.1rem 0.3rem",
				background: "#cbffb2",
				color: "#215109",
				borderRadius: "1rem",
				boxShadow:
					"0 0 0 1px #72fd4e80,0 16px 8px #72fd4e03,0 12px 6px #72fd4e0a,0 4px 4px #72fd4e12,0 1.5px 3px #72fd4e14,inset 0 1px 2px #ffffff1f",
			}}
		>
			-{percentage}%
		</div>
	)

	useEffect(() => {
		const update = () => {
			if (!anchorRef.current) return
			if (!labelRef.current) return

			const anchorRect = anchorRef.current.getBoundingClientRect()
			const labelRect = labelRef.current.getBoundingClientRect()

			setPos({
				top: anchorRect.top - labelRect.height - 10,
				left:
					anchorRect.left -
					labelRect.width / 2 +
					anchorRect.width / 2,
			})
		}

		const resizeObs = new ResizeObserver(update)
		if (anchorRef.current) {
			resizeObs.observe(anchorRef.current)
		}
		window.addEventListener("resize", update)

		update()

		return () => {
			resizeObs.disconnect()
			window.removeEventListener("resize", update)
		}
	}, [])

	return (
		<div style={{ position: "relative" }}>
			<SegmentedControl
				color="dark"
				value={billingPeriod}
				onChange={setBillingPeriod}
				data={[
					{
						label: t("components.billing_checkout.monthly"),
						value: "monthly",
					},
					{
						label: (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "0.5rem",
								}}
							>
								{t("components.billing_checkout.quarterly")}
								{additionalSavings?.quarterly
									? savingsBadge(additionalSavings.quarterly)
									: null}
								{showDiscounts && !additionalSavings && (
									<div
										style={{
											fontSize: "0.65rem",
											padding: "0.1rem 0.3rem",
											background: discountBackgroundColor,
											color: discountTextColor,
											borderRadius: "1rem",
											boxShadow: discountBoxShadow,
										}}
									>
										{!disableDiscountsForCartItems
											? "-"
											: null}
										{disableDiscountsForCartItems
											? 0
											: state.discounts
													?.essential_new_users
													?.quarterly
													?.discountValuePercentage ||
												0}
										%
									</div>
								)}
							</div>
						),
						value: "quarterly",
					},
					{
						label: (
							<div
								ref={anchorRef}
								style={{
									display: "flex",
									alignItems: "center",
									gap: "0.5rem",
								}}
							>
								{t("components.billing_checkout.yearly")}
								{additionalSavings?.yearly
									? savingsBadge(additionalSavings.yearly)
									: null}
								{showDiscounts && !additionalSavings && (
									<div
										style={{
											fontSize: "0.65rem",
											padding: "0.1rem 0.3rem",
											background: discountBackgroundColor,
											color: discountTextColor,
											borderRadius: "1rem",
											boxShadow: discountBoxShadow,
										}}
									>
										{!disableDiscountsForCartItems
											? "-"
											: null}
										{disableDiscountsForCartItems
											? 0
											: state.discounts
													?.essential_new_users
													?.yearly
													?.discountValuePercentage ||
												0}
										%
									</div>
								)}
							</div>
						),
						value: "yearly",
					},
				]}
				styles={(theme) => ({
					root: {
						// background: "#ccc",
						padding: "0.2rem 0.3rem",
						paddingLeft: "0.25rem",
						borderRadius: "3rem",
						paddingBottom: "0.1rem",
						border: "1px solid #dfdfdf",
					},
					indicator: {
						background: "#111",
						borderRadius: "3.3rem",
						// border: "1px solid #999",
						// transform: "none !important",
					},
					label: {
						padding: "0.2rem 0.6rem",
						paddingBottom: "0.3rem",
					},
					control: {
						// width: "10rem",
						borderWidth: "0px !important",
					},
				})}
			/>

			{showDiscounts
				? createPortal(
						<div
							ref={labelRef}
							style={{
								position: "absolute",
								top: pos.top,
								left: pos.left,
								background: "#333",
								padding: "0.1rem 0.5rem",
								borderRadius: "1rem",
							}}
						>
							<div
								style={{
									fontSize: "0.6rem",
									fontWeight: "600",
									color: "#fff",
								}}
							>
								{t("components.billing_bundles.most_popular")}
							</div>
						</div>,
						document.body,
					)
				: null}
		</div>
	)
}
